import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@material-ui/core';
import { Form } from '@unform/web';
import moment from 'moment';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Select, Autocomplete } from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import api from '~/services/api';
import { storeData } from '~/store/modules/fechamentolmc/actions';

import * as S from './styles';

function Fechamentolmc() {
  const params = useRef(null);
  const reference = useRef(null);
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const [loading, setLoading] = useState(false);

  // campos
  const [posto, setPosto] = useState(null);
  const [ano, setAno] = useState(moment().year());
  const [mes, setMes] = useState(moment().month() + 1);
  const [sequencial, setSequencial] = useState(0);
  const [observacao, setObservacao] = useState('');

  // combobox de anos
  const anoInicial = 2018;
  const anoAtual = new Date().getFullYear();

  const anos = [];

  for (let ano = anoInicial; ano <= anoAtual; ano++) {
    anos.push({ label: ano.toString(), value: ano });
  }

  // combobox de meses
  const meses = [
    { value: '1', label: t('s.geral.opcoes.janeiro') },
    { value: '2', label: t('s.geral.opcoes.fevereiro') },
    { value: '3', label: t('s.geral.opcoes.marco') },
    { value: '4', label: t('s.geral.opcoes.abril') },
    { value: '5', label: t('s.geral.opcoes.maio') },
    { value: '6', label: t('s.geral.opcoes.junho') },
    { value: '7', label: t('s.geral.opcoes.julho') },
    { value: '8', label: t('s.geral.opcoes.agosto') },
    { value: '9', label: t('s.geral.opcoes.setembro') },
    { value: '10', label: t('s.geral.opcoes.outubro') },
    { value: '11', label: t('s.geral.opcoes.novembro') },
    { value: '12', label: t('s.geral.opcoes.dezembro') },
  ];

  const initials = { anolmc: ano, meslmc: mes };

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idposto: Yup.number()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio')),
        anolmc: Yup.number().required(t('s.geral.validacao.campoobrigatorio')),
        meslmc: Yup.number().required(t('s.geral.validacao.campoobrigatorio')),
        sequenciallmc: Yup.number().nullable(),
        observacaolmc: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(255, t('s.geral.validacao.tamanhomaximo').replace('%s', 255))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
        idposto: posto,
        anolmc: ano,
        meslmc: mes,
        sequenciallmc: sequencial,
        observacaolmc: observacao,
      };

      await dispatch(storeData(objSend, params));
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  async function handleValidaSequencial() {
    if (!posto || !ano || !mes) {
      setSequencial(0);
      setObservacao('');
      return;
    }
    const response = await api.get(`fechamentolmc/${posto}/${ano}/${mes}`);
    if (response) {
      const { data } = response;
      if (data) {
        setSequencial(data.sequenciallmc);
        setObservacao(data.observacaolmc);
        reference.current.setFieldValue('observacaolmc', data.observacaolmc);
      }
    }
  }

  useEffect(() => {
    handleValidaSequencial();
  }, [posto, ano, mes]);

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <S.Wrapper>
            <S.WrapperInputs>
              <S.WrapperGroupInput>
                <Autocomplete
                  name="idposto"
                  label="codigonomeposto"
                  url="postoscomboios"
                  params={{
                    wherefield: ['tipoposto'],
                    valuewherefield: ['P'],
                    typewherefield: ['EQUALS'],
                  }}
                  unique="idposto"
                  title={t('s.menu.configuracao.posto')}
                  getValue={(value) => {
                    if (value) {
                      setPosto(value.idposto);
                    } else {
                      setPosto(null);
                    }
                  }}
                />
              </S.WrapperGroupInput>
              <S.WrapperGroupInput>
                <Select
                  name="anolmc"
                  value={ano}
                  label={t('s.geral.ano')}
                  options={anos}
                  getValue={(value) => {
                    setAno(value);
                  }}
                />
              </S.WrapperGroupInput>
              <S.WrapperGroupInput>
                <Select
                  name="meslmc"
                  value={mes}
                  label={t('s.geral.mes')}
                  options={meses}
                  getValue={(value) => {
                    setMes(value);
                  }}
                />
              </S.WrapperGroupInput>
              <S.WrapperGroupInput>
                <Input
                  name="sequenciallmc"
                  value={sequencial}
                  label={t('s.geral.sequencial')}
                  type="number"
                  disabled
                />
              </S.WrapperGroupInput>
              <S.WrapperGroupInput>
                <Input
                  name="observacaolmc"
                  value={observacao}
                  label={t('s.geral.observacao')}
                  multiline
                  rows={3}
                  inputProps={{ maxLength: 255 }}
                  getValue={(value) => {
                    setObservacao(value);
                  }}
                />
              </S.WrapperGroupInput>
            </S.WrapperInputs>
          </S.Wrapper>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            fullWidth={false}
            type="submit"
          >
            {t('s.geral.botaoconfirmar')}
          </Button>
        </Form>
      )}
    </>
  );
}

export default Fechamentolmc;
